import React, { useCallback, useEffect, useState } from "react";
import { ProjectOverviewDto } from "../../../types/projectTypes";
import AllProjectsTable from "./AllProjectsTable";
import { Box, IconButton, InputAdornment, SelectChangeEvent, TextField } from "@mui/material";
import { FilterListOff, Search } from "@mui/icons-material";
import FilterList from "@mui/icons-material/FilterList";
import useResponsive from "../../../hooks/useResponsive";
import AllProjectsDateFilters from "./AllProjectsDateFilters";
import AllProjectsCoreCompetenciesFilter from "./AllProjectsCoreCompetenciesFilter";
import dayjs, { Dayjs } from "dayjs";
import CsvLinkButton from "../../general/CsvLinkButton";
import useProjectLeads from "../../../hooks/projectLeads/useProjectLeads";
import ProjectLeadsList from "../../projectLeads/ProjectLeadsList";

interface AllProjectsProps {
  projects: ProjectOverviewDto[];
}

/**
 * Component to display all projects, a search bar and a filter button
 * @param projects - List of projects
 * @returns - A table with all projects, a search bar and a filter button
 */
const AllProjects = ({ projects }: AllProjectsProps) => {
  const { projectLeads } = useProjectLeads();
  const [filteredProjects, setFilteredProjects] = React.useState<ProjectOverviewDto[]>(projects);
  const [searchTerm, setSearchTerm] = React.useState("");
  const [showFilters, setShowFilters] = React.useState(false);
  const [startDate, setStartDate] = React.useState<Dayjs | null>(null);
  const [endDate, setEndDate] = React.useState<Dayjs | null>(null);
  const isMobile = useResponsive("down", "sm");
  const coreCompetencies = [
    "Finance & Controlling",
    "Human Resources",
    "Marketing & Sales",
    "Process & Organization",
    "Research & Implications",
    "Sustainability",
    "Sonstiges",
  ];
  const [coreCompetenciesFilter, setCoreCompetenciesFilter] = useState<string[]>(coreCompetencies.sort());
  const [sortedBy, setSortedBy] = useState("");

  // Show or hide the filter options
  const handleShowFilters = () => {
    setShowFilters(!showFilters);
  };

  // Change the search term
  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
  };

  // Change the start date
  const onChangeStartDate = (date: Dayjs | null) => {
    setStartDate(date);
  };

  // Change the end date
  const onChangeEndDate = (date: Dayjs | null) => {
    setEndDate(date);
  };

  // Change the core competencies filter
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const onChangeCoreCompetenciesFilter = (event: SelectChangeEvent<any>) => {
    const value = event.target.value;
    if (coreCompetenciesFilter.includes(value)) {
      setCoreCompetenciesFilter(coreCompetenciesFilter.filter((competence) => competence !== value));
    } else {
      setCoreCompetenciesFilter([value, ...coreCompetenciesFilter]);
    }
  };

  // Reset the core competencies filter
  const resetProjectFilters = () => {
    setCoreCompetenciesFilter(coreCompetencies);
    setSearchTerm("");
    setFilteredProjects(projects);
  };

  // Filter the projects based on the search term, start date, end date and core competencies
  useEffect(() => {
    let filteredProjects = projects.filter((project) =>
      project.projectName.toLowerCase().includes(searchTerm.toLowerCase())
    );
    if (startDate) {
      filteredProjects = filteredProjects.filter((project) => {
        return project.kickoff
          ? dayjs(project.kickoff) >= startDate
          : dayjs(project.tenderDate)
          ? dayjs(project.tenderDate) >= startDate
          : false;
      });
    }
    if (endDate) {
      filteredProjects = filteredProjects.filter((project) => {
        return project.projectEnd ? dayjs(project.projectEnd) <= endDate : false;
      });
    }
    // checks wether the project has at least one core competency that is in the coreCompetenciesFilter
    filteredProjects = filteredProjects.filter((project) =>
      project.coreCompetencies.some((competence) => coreCompetenciesFilter.includes(competence.designation))
    );
    setFilteredProjects(filteredProjects);
  }, [searchTerm, startDate, endDate, coreCompetenciesFilter, projects]);

  // Sort the projects
  const handleSortBy = (sortBy: string) => {
    const sortedProjects = [...filteredProjects];
    sortedProjects.sort((a, b) => {
      if (sortBy === "BT descending") {
        // calculate average of estimated BT
        const aBT = (a.estimatedProjectBTmin + a.estimatedProjectBTmax) / 2;
        const bBT = (b.estimatedProjectBTmin + b.estimatedProjectBTmax) / 2;
        // sort descending compare soldBT with soldBT or estimated BT with soldBT if soldBT is null or estimated BT with estimated BT if both soldBT is null
        if (a.soldBT === null && b.soldBT === null) {
          return aBT - bBT;
        }
        if (a.soldBT != null && b.soldBT != null) {
          return a.soldBT - b.soldBT;
        }
        if (a.soldBT === null && b.soldBT != null) {
          return aBT - b.soldBT;
        }
        if (b.soldBT === null && a.soldBT != null) {
          return a.soldBT - bBT;
        }
      }
      if (sortBy === "BT ascending") {
        // calculate average of estimated BT
        const aBT = (a.estimatedProjectBTmin + a.estimatedProjectBTmax) / 2;
        const bBT = (b.estimatedProjectBTmin + b.estimatedProjectBTmax) / 2;
        // sort ascending compare soldBT with soldBT or estimated BT with soldBT if soldBT is null or estimated BT with estimated BT if both soldBT is null
        if (a.soldBT === null && b.soldBT === null) {
          return bBT - aBT;
        }
        if (a.soldBT != null && b.soldBT != null) {
          return b.soldBT - a.soldBT;
        }
        if (a.soldBT === null && b.soldBT != null) {
          return b.soldBT - aBT;
        }
        if (b.soldBT === null && a.soldBT != null) {
          return bBT - a.soldBT;
        }
      }
      if (sortBy === "Beginn descending") {
        // sort descending compare kickoff with kickoff or tenderDate with kickoff if kickoff is null or tenderDate with tenderDate if both kickoff is null
        if (a.kickoff === null && b.kickoff === null) {
          return dayjs(a.tenderDate).diff(dayjs(b.tenderDate));
        }
        if (a.kickoff != null && b.kickoff != null) {
          return dayjs(a.kickoff).diff(dayjs(b.kickoff));
        }
        if (a.kickoff === null && b.kickoff != null) {
          return dayjs(a.tenderDate).diff(dayjs(b.kickoff));
        }
        if (b.kickoff === null && a.kickoff != null) {
          return dayjs(a.kickoff).diff(dayjs(b.tenderDate));
        }
      }
      if (sortBy === "Beginn ascending") {
        // sort ascending compare kickoff with kickoff or tenderDate with kickoff if kickoff is null or tenderDate with tenderDate if both kickoff is null
        if (a.kickoff === null && b.kickoff === null) {
          return dayjs(b.tenderDate).diff(dayjs(a.tenderDate));
        }
        if (a.kickoff != null && b.kickoff != null) {
          return dayjs(b.kickoff).diff(dayjs(a.kickoff));
        }
        if (a.kickoff === null && b.kickoff != null) {
          return dayjs(b.kickoff).diff(dayjs(a.tenderDate));
        }
        if (b.kickoff === null && a.kickoff != null) {
          return dayjs(b.tenderDate).diff(dayjs(a.kickoff));
        }
      }
      if (sortBy === "Ende descending") {
        return a.projectEnd === null ? 1 : b.projectEnd === null ? -1 : dayjs(b.projectEnd).diff(a.projectEnd);
      }
      if (sortBy === "Ende ascending")
        return a.projectEnd === null ? -1 : b.projectEnd === null ? 1 : dayjs(a.projectEnd).diff(b.projectEnd);
      if (sortBy === "Projektname descending") {
        return a.projectName.localeCompare(b.projectName);
      }
      if (sortBy === "Projektname ascending") return b.projectName.localeCompare(a.projectName);
      if (sortBy === "Branche descending") {
        return a.client.industry === null
          ? 1
          : b.client.industry === null
          ? -1
          : a.client.industry.description.localeCompare(b.client.industry.description);
      }
      if (sortBy === "Branche ascending")
        return a.client.industry === null
          ? -1
          : b.client.industry === null
          ? 1
          : b.client.industry.description.localeCompare(a.client.industry.description);
      if (sortBy === "Unternehmen descending") {
        return b.client === null
          ? 1
          : a.client === null
          ? -1
          : a.client.companyName.localeCompare(b.client.companyName);
      }
      if (sortBy === "Unternehmen ascending")
        return b.client === null
          ? -1
          : a.client === null
          ? 1
          : b.client.companyName.localeCompare(a.client.companyName);
      if (sortBy === "Kernkompetenz descending") {
        const aDesignation = a.coreCompetencies[0]?.designation ?? "";
        const bDesignation = b.coreCompetencies[0]?.designation ?? "";
        return aDesignation.localeCompare(bDesignation);
      }
      if (sortBy === "Kernkompetenz ascending") {
        const aDesignation = a.coreCompetencies[0]?.designation ?? "";
        const bDesignation = b.coreCompetencies[0]?.designation ?? "";
        return bDesignation.localeCompare(aDesignation);
      }
      if (sortBy === "Status descending") {
        return a.status.localeCompare(b.status);
      }
      if (sortBy === "Status ascending") return b.status.localeCompare(a.status);
      return 0;
    });
    setFilteredProjects(sortedProjects);
  };

  // Sort the projects when the sorting option changes
  const onChangeSortBy = (sortBy: string) => {
    setSortedBy(sortBy);
    handleSortBy(sortBy);
  };

  /**
   * Transforms the projects to the data format for the CSV export
   * @param projects - The projects to transform
   * @returns - The transformed projects
   */
  const getDataForCsvExport = useCallback(() => {
    return filteredProjects.map((project) => ({
      ID: project.projectId,
      Name: project.projectName,
      Branche: project.client.industry.description,
      Unternehmen: project.client.companyName,
      Kernkompetenzen: project.coreCompetencies.map((competency) => competency.designation).join(", "),
      Kickoff: project.kickoff,
      Ende: project.projectEnd,
      "Verkaufte BT": project.soldBT,
      "Geschätzte BT": project.estimatedProjectBTmin,
      Status: project.status,
    }));
  }, [filteredProjects]);

  return (
    <>
      <Box sx={{ display: "flex", flex: 1, flexDirection: "row" }}>
        <IconButton sx={{ height: 40, width: 40 }} onClick={handleShowFilters}>
          {showFilters ? <FilterListOff /> : <FilterList />}
        </IconButton>
        <CsvLinkButton data={getDataForCsvExport()} fileName="projects_export" />
        {showFilters && !isMobile ? (
          <AllProjectsDateFilters
            resetProjectFilters={resetProjectFilters}
            startDate={startDate}
            onChangeStartDate={onChangeStartDate}
            endDate={endDate}
            onChangeEndDate={onChangeEndDate}
          />
        ) : null}
        <TextField
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search />
              </InputAdornment>
            ),
          }}
          value={searchTerm}
          onChange={handleSearch}
          variant="outlined"
          size="small"
          placeholder="Projekt suchen..."
          sx={{ marginLeft: "auto", mb: 2 }}
        />
      </Box>
      {showFilters && isMobile ? (
        <AllProjectsDateFilters
          resetProjectFilters={resetProjectFilters}
          startDate={startDate}
          onChangeStartDate={onChangeStartDate}
          endDate={endDate}
          onChangeEndDate={onChangeEndDate}
        />
      ) : null}
      {showFilters ? (
        <AllProjectsCoreCompetenciesFilter
          coreCompetenciesFilter={coreCompetenciesFilter}
          onChangeCoreCompetenciesFilter={onChangeCoreCompetenciesFilter}
          setCoreCompetenciesFilter={setCoreCompetenciesFilter}
        />
      ) : null}
      <ProjectLeadsList projectLeads={projectLeads} />
      <AllProjectsTable projects={filteredProjects} sortedBy={sortedBy} onChangeSortBy={onChangeSortBy} />
    </>
  );
};

export default AllProjects;
